import { type ChangeEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import Loading from '../../common/lib/Loading';
import type { MultiLang } from '../../config';
import Functions from '../../functions';
import ItemType from '../item-type';
import {
    type Item,
    type SearchFunc,
    type SearchResult,
    type SortCondition,
    type SortConditionLimit,
    type SortConditionOrderBy,
    SortConditionOrderDir,
} from './ItemUtil';
import styles from './XoonipsListItem.module.css';

const SORT_CONDITION_DEFAULT: SortCondition = {
    limit: 20,
    page: 1,
    orderBy: 'title',
    orderDir: 1,
};
const SORT_CONDITION_RANGE_ORDER_BY = ['title', 'doi', 'last_update_date', 'creation_date', 'publication_date'];
const SORT_CONDITION_RANGE_ORDER_DIR = ['0', '1'];
const SORT_CONDITION_RANGE_LIMIT = ['20', '50', '100'];

interface Props {
    lang: MultiLang;
    url: string;
    search: SearchFunc;
}

const getSortConditionByQuery = (queryString: string): SortCondition => {
    const query = new URLSearchParams(queryString);
    const condition = { ...SORT_CONDITION_DEFAULT };
    const orderby = query.get('orderby');
    if (orderby !== null && SORT_CONDITION_RANGE_ORDER_BY.includes(orderby)) {
        condition.orderBy = orderby as SortConditionOrderBy;
    }
    const order_dir = query.get('order_dir');
    if (order_dir !== null && SORT_CONDITION_RANGE_ORDER_DIR.includes(order_dir)) {
        condition.orderDir = parseInt(order_dir, 10) as SortConditionOrderDir;
    }
    const itemcount = query.get('itemcount');
    if (itemcount !== null && SORT_CONDITION_RANGE_LIMIT.includes(itemcount)) {
        condition.limit = parseInt(itemcount, 10) as SortConditionLimit;
    }
    const page = query.get('page');
    if (page !== null && page.match(/^\d+$/) !== null) {
        condition.page = parseInt(page, 10);
    }
    return condition;
};

const XoonipsListItem = (props: Props) => {
    const { lang, url, search } = props;
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState<SearchResult>({ total: 0, data: [] });

    const condition = getSortConditionByQuery(location.search);
    const { limit, page, orderBy, orderDir } = condition;

    // `search` is memoized by every caller, so it is safe as an effect dependency.
    useEffect(() => {
        let active = true;
        search({ limit, page, orderBy, orderDir }, (searchResult) => {
            if (active) {
                setResult(searchResult);
                setLoading(false);
            }
        });
        return () => {
            active = false;
        };
    }, [search, limit, page, orderBy, orderDir]);

    const getListUrl = (query: { orderby?: string; order_dir?: number; limit?: number; page?: number }) => {
        const newOrderBy = typeof query.orderby !== 'undefined' ? query.orderby : condition.orderBy;
        const newOrderDir = typeof query.order_dir !== 'undefined' ? query.order_dir : condition.orderDir;
        const newLimit = typeof query.limit !== 'undefined' ? query.limit : condition.limit;
        let newPage = typeof query.page !== 'undefined' ? query.page : condition.page;
        if (newLimit !== condition.limit || newOrderBy !== condition.orderBy) {
            newPage = SORT_CONDITION_DEFAULT.page;
        }
        const params = new URLSearchParams({
            orderby: newOrderBy,
            order_dir: String(newOrderDir),
            itemcount: String(newLimit),
            page: String(newPage),
        });
        const join = url.indexOf('?') < 0 ? '?' : '&';
        return url + join + params.toString();
    };

    const handleSelectOrderby = (event: ChangeEvent<HTMLSelectElement>) => {
        void navigate(getListUrl({ orderby: event.target.value }));
    };

    const handleSelectItemcount = (event: ChangeEvent<HTMLSelectElement>) => {
        void navigate(getListUrl({ limit: parseInt(event.target.value, 10) }));
    };

    const renderSortCriteria = () => {
        let orderDirElement: React.JSX.Element;
        if (condition.orderDir !== SortConditionOrderDir.ASC) {
            const dirUrl = getListUrl({ order_dir: SortConditionOrderDir.ASC });
            orderDirElement = (
                <span>
                    &#9660;&nbsp;<Link to={dirUrl}>&#9650;</Link>
                </span>
            );
        } else {
            const dirUrl = getListUrl({ order_dir: SortConditionOrderDir.DESC });
            orderDirElement = (
                <span>
                    <Link to={dirUrl}>&#9660;</Link>&nbsp;&#9650;
                </span>
            );
        }
        return (
            <div className={styles.sortCriteria}>
                <table className="listTable">
                    <tbody>
                        <tr>
                            <td className="odd">
                                Order by&nbsp;
                                <select value={condition.orderBy} onChange={handleSelectOrderby}>
                                    <option value="title">
                                        {Functions.mlang('[en]Title[/en][ja]タイトル[/ja]', lang)}
                                    </option>
                                    <option value="doi">ID</option>
                                    <option value="last_update_date">
                                        {Functions.mlang('[en]Last Modified Date[/en][ja]最終更新日[/ja]', lang)}
                                    </option>
                                    <option value="creation_date">
                                        {Functions.mlang('[en]Created Date[/en][ja]作成日[/ja]', lang)}
                                    </option>
                                    <option value="publication_date">
                                        {Functions.mlang('[en]Date[/en][ja]日付[/ja]', lang)}
                                    </option>
                                </select>
                                &nbsp;{orderDirElement}
                            </td>
                            <td className="odd">
                                No.Item per page&nbsp;
                                <select value={condition.limit} onChange={handleSelectItemcount}>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const renderPageNavi = (searchResult: SearchResult) => {
        const maxPage =
            Math.floor(searchResult.total / condition.limit) + (searchResult.total % condition.limit !== 0 ? 1 : 0);
        const link = (title: string, linkPage: number, id: string) => {
            if (linkPage === 0 || linkPage === condition.page) {
                return (
                    <span key={id} className={styles.pageNaviItem}>
                        {title}
                    </span>
                );
            }
            const pageUrl = getListUrl({ page: linkPage });
            return (
                <Link key={id} className={styles.pageNaviItem} to={pageUrl}>
                    {title}
                </Link>
            );
        };
        let startPage = condition.page - 4 > 1 ? condition.page - 4 : 1;
        const endPage = startPage + 9 > maxPage ? maxPage : startPage + 9;
        if (endPage - startPage < 9) {
            startPage = endPage - 9 > 0 ? endPage - 9 : 1;
        }
        const pageLinks: React.JSX.Element[] = [];
        pageLinks.push(link('PREV', condition.page - 1, 'p'));
        for (let i = startPage; i <= endPage; i++) {
            pageLinks.push(link(String(i), i, String(i)));
        }
        pageLinks.push(link('NEXT', condition.page >= maxPage ? 0 : condition.page + 1, 'n'));
        return <div className={styles.pageNavi}>{pageLinks}</div>;
    };

    if (loading) {
        return <Loading />;
    }
    if (result.data.length === 0) {
        return <p>No items found.</p>;
    }
    const startNum = 1 + condition.limit * (condition.page - 1);
    let endNum = condition.limit * condition.page;
    if (endNum > result.total) {
        endNum = result.total;
    }
    const pageNavi = renderPageNavi(result);
    const items = result.data.map((item: Item, idx) => {
        const evenodd = idx % 2 === 0 ? 'even' : 'odd';
        return (
            <tr key={item.item_id} className={evenodd}>
                <td>
                    <ItemType.List lang={lang} item={item} />
                </td>
            </tr>
        );
    });
    return (
        <>
            {renderSortCriteria()}
            <p>
                {startNum} - {endNum} of {result.total} Items
            </p>
            {pageNavi}
            <table className="listTable">
                <tbody>{items}</tbody>
            </table>
            {pageNavi}
        </>
    );
};

export default XoonipsListItem;
